import React, { useEffect } from 'react';
import './predictorLanding.css';

const SOURCES = [
  {
    icon: 'fa-plane',
    label: 'Flight Data',
    color: '#4f8ef7',
    desc: 'Real-time flight status, cancellations, delays, and arrival volumes from all feeder airports.',
  },
  {
    icon: 'fa-cloud',
    label: 'Weather Intelligence',
    color: '#22d3ee',
    desc: 'Severe weather events, tropical storms, and localized conditions that disrupt travel plans.',
  },
  {
    icon: 'fa-newspaper-o',
    label: 'News & Events',
    color: '#f59e0b',
    desc: 'Local events, strikes, infrastructure incidents, and breaking news that affect arrivals.',
  },
];

const FEATURES = [
  { icon: 'fa-clock-o', title: '72-Hour Window', desc: 'Get disruption forecasts up to 3 days ahead. Enough lead time to adjust staffing, set expectations, and protect revenue.' },
  { icon: 'fa-bolt', title: 'Real-Time Alerts', desc: 'Instant notifications when a disruption risk crosses your threshold. No dashboards to check &mdash; it finds you.' },
  { icon: 'fa-sliders', title: 'Risk Score per Day', desc: 'Each day in the forecast window gets a clear risk score. Low, Medium, High. No ambiguity, just actionable signal.' },
  { icon: 'fa-users', title: 'Staffing Recommendations', desc: 'Predictor suggests staffing adjustments based on forecasted disruption level, so you can act before it happens.' },
  { icon: 'fa-line-chart', title: 'Historical Patterns', desc: 'Model trained on years of hospitality data to understand how each disruption type affects your property type.' },
  { icon: 'fa-building', title: 'Property-Level Tuning', desc: 'Calibrated to your location, feeder markets, and seasonality. Not a generic model &mdash; tuned to your reality.' },
];

const TIMELINE = [
  { hour: 'Now', risk: 'low',    label: 'Normal arrivals expected' },
  { hour: '+24h', risk: 'medium', label: 'Weather advisory: Caribbean' },
  { hour: '+48h', risk: 'high',   label: '4 flights cancelled CUN hub' },
  { hour: '+72h', risk: 'medium', label: 'Partial recovery expected' },
];

const RISK_COLORS = { low: '#10b981', medium: '#f59e0b', high: '#f43f5e' };

export const PredictorLanding = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Predictor | 72-Hour Arrivals Intelligence by Mindware';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="pr-page">

      {/* ── Nav ── */}
      <nav className="pr-nav">
        <div className="pr-nav-inner">
          <a href="/predictor" className="pr-nav-brand">
            <i className="fa fa-bolt" />
            <span>Predictor</span>
            <span className="pr-by">by Mindware</span>
          </a>
          <div className="pr-nav-links">
            <a href="#pr-how">How it works</a>
            <a href="#pr-features">Features</a>
            <a href="#pr-cta" className="pr-nav-cta">Request Early Access</a>
          </div>
          <a href="/" className="pr-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pr-hero">
        <div className="pr-hero-bg" />
        <div className="pr-hero-grid" />
        <div className="pr-wrap pr-hero-inner">
          <div className="pr-hero-copy">
            <div className="pr-hero-badge">
              <span className="pr-badge-dot" />
              Early Access &mdash; Hospitality
            </div>
            <span className="pr-eyebrow">Arrivals Intelligence Platform</span>
            <h1>
              Know before
              <br />
              <span className="pr-grad">disruption hits.</span>
            </h1>
            <p>
              Predictor analyzes flight data, weather patterns, and live news
              to forecast arrival disruptions up to <strong>72 hours in advance</strong>.
              Built for hotels, resorts, and hospitality operations that can't
              afford to be caught off guard.
            </p>
            <div className="pr-hero-actions">
              <a href="#pr-cta" className="pr-btn-primary">Request Early Access</a>
              <a href="#pr-how" className="pr-btn-ghost">See How It Works</a>
            </div>
          </div>
          <div className="pr-hero-visual">
            <div className="pr-timeline-card">
              <div className="pr-timeline-header">
                <i className="fa fa-bolt" /> 72-Hour Forecast
                <span className="pr-timeline-sub">Updated 3 min ago</span>
              </div>
              {TIMELINE.map((t, i) => (
                <div key={i} className="pr-timeline-row">
                  <div className="pr-timeline-hour">{t.hour}</div>
                  <div className="pr-timeline-bar-wrap">
                    <div
                      className="pr-timeline-bar"
                      style={{
                        width: t.risk === 'high' ? '85%' : t.risk === 'medium' ? '55%' : '25%',
                        background: RISK_COLORS[t.risk],
                      }}
                    />
                  </div>
                  <div className="pr-timeline-info">
                    <span className="pr-risk-pill" style={{ background: RISK_COLORS[t.risk] + '22', color: RISK_COLORS[t.risk], border: `1px solid ${RISK_COLORS[t.risk]}44` }}>
                      {t.risk.charAt(0).toUpperCase() + t.risk.slice(1)}
                    </span>
                    <small>{t.label}</small>
                  </div>
                </div>
              ))}
              <div className="pr-timeline-sources">
                <span><i className="fa fa-plane" /> Flights</span>
                <span><i className="fa fa-cloud" /> Weather</span>
                <span><i className="fa fa-newspaper-o" /> News</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="pr-how" className="pr-section pr-how-section">
        <div className="pr-wrap">
          <div className="pr-section-head">
            <span className="pr-label">How It Works</span>
            <h2>Three data streams. One clear forecast.</h2>
            <p>Predictor continuously ingests signals from multiple sources, runs them through a hospitality-trained model, and surfaces actionable forecasts.</p>
          </div>
          <div className="pr-sources">
            {SOURCES.map((s, i) => (
              <div key={i} className="pr-source-card" style={{ '--accent': s.color }}>
                <div className="pr-source-icon" style={{ background: s.color + '20', color: s.color }}>
                  <i className={`fa ${s.icon}`} />
                </div>
                <h3>{s.label}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="pr-flow">
            <div className="pr-flow-item pr-flow-item--sources">
              <span>Data Ingestion</span>
              <small>Flights + Weather + News, continuously</small>
            </div>
            <div className="pr-flow-arrow">&#8594;</div>
            <div className="pr-flow-item pr-flow-item--model">
              <span>Intelligence Layer</span>
              <small>Hospitality-trained ML model</small>
            </div>
            <div className="pr-flow-arrow">&#8594;</div>
            <div className="pr-flow-item pr-flow-item--output">
              <span>72-Hour Forecast</span>
              <small>Risk scores + actionable alerts</small>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="pr-features" className="pr-section pr-features-section">
        <div className="pr-wrap">
          <div className="pr-section-head">
            <span className="pr-label">Features</span>
            <h2>Built for operations teams, not data scientists</h2>
            <p>No complex setup. No models to train. Just clear forecasts your team can act on.</p>
          </div>
          <div className="pr-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="pr-feat-card">
                <i className={`fa ${f.icon} pr-feat-icon`} />
                <h3>{f.title}</h3>
                <p dangerouslySetInnerHTML={{ __html: f.desc }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Problem / Quote ── */}
      <section className="pr-section pr-quote-section">
        <div className="pr-wrap pr-quote-inner">
          <div className="pr-quote-mark">&ldquo;</div>
          <blockquote>
            Hotels lose thousands in revenue every time an unexpected event
            disrupts arrivals. Flight cancellations, weather events, local
            incidents &mdash; they&apos;re all predictable with the right data.
            Most properties don&apos;t have it. Predictor fixes that.
          </blockquote>
          <cite>Mindware, 2025 &mdash; based on analysis across resort markets in Mexico</cite>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="pr-cta" className="pr-section pr-cta-section">
        <div className="pr-wrap pr-cta-inner">
          <span className="pr-label">Early Access</span>
          <h2>Be first to know, every time.</h2>
          <p>
            Predictor is currently in early access for hotel and resort properties.
            Contact us to schedule a demo and join the program.
          </p>
          <a href="mailto:contact@mindware.com.mx" className="pr-btn-primary pr-btn-lg">
            Request Early Access &nbsp;<i className="fa fa-arrow-right" />
          </a>
          <div className="pr-cta-sub">
            <a href="mailto:contact@mindware.com.mx">contact@mindware.com.mx</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="pr-footer">
        <div className="pr-wrap pr-footer-inner">
          <span>Predictor by <a href="/">Mindware</a></span>
          <a href="/">&#8592; Back to Mindware</a>
        </div>
      </footer>

    </div>
  );
};
